import { useRef } from 'react'

import { jingleCollectionByIdQuery } from '@/entities/jingle-collections'
import { TrackUploadStatus } from '@/entities/tracks'
import { type CreateJingleCollectionItemModel } from '@/shared/api'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import { EmptyContent, HidingSpin, TableDragHandle } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import {
  CloseCircleTwoTone,
  CloseOutlined,
  EditOutlined,
  ExclamationCircleTwoTone,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { DragSortTable } from '@ant-design/pro-components'
import type { ProColumns } from '@ant-design/pro-components'
import { Button, Flex, Space, Spin, Tooltip, Upload, theme } from 'antd'
import { useUnit } from 'effector-react'

import {
  jingleAddToCollection,
  jingleCollectionJinglesOrderUpdate,
} from '../api'
import {
  $jingleCollectionJinglesReorderBlocked,
  $jingleCollectionJinglesWithProgressReordered,
  type JingleCollectionJingleWithStatus,
  addJingleCollectionJingleMultiuploadFiles,
  jingleCollectionJinglesReordered,
  jingleRemovedFromCollection,
  jinglesJingleCollectionJinglesQuery,
} from '../model/jingle-collection-jingle-multiupload.model'
import { jingleSelectionModalOpened } from '../model/jingle-selection-modal.model'
import { JingleSelectionModal } from './JingleSelectionModal'

interface JingleCollectionFormJinglesProps {
  onJingleEditButtonClick: (id: string) => void
}

export const JingleCollectionFormJingles = ({
  onJingleEditButtonClick,
}: JingleCollectionFormJinglesProps) => {
  const { token: antDesignThemeToken } = theme.useToken()

  // Data

  const jingleCollectionData = useUnit(jingleCollectionByIdQuery.$data)
  const jingleCollectionError = useUnit(jingleCollectionByIdQuery.$error)
  const jingleCollectionJinglesData = useUnit(
    jinglesJingleCollectionJinglesQuery.$data,
  )
  const jingleCollectionJinglesError = useUnit(
    jinglesJingleCollectionJinglesQuery.$error,
  )
  const addJingleToCollection = useUnit(jingleAddToCollection)

  const jingleCollectionJinglesList = useUnit(
    $jingleCollectionJinglesWithProgressReordered,
  )

  // Selection modal
  const openJingleSelectionModal = useUnit(jingleSelectionModalOpened)

  // Jingles files upload
  const addFiles = useUnit(addJingleCollectionJingleMultiuploadFiles)

  // Refs
  const uploadRef = useRef<HTMLDivElement>(null)

  // JingleCollection Jingles Reorder
  const updateJinglePositionInJingleCollection = useUnit(
    jingleCollectionJinglesOrderUpdate,
  )
  const reorderJingleCollectionJingles = useUnit(
    jingleCollectionJinglesReordered,
  )
  const handleDragSortEnd = (
    _beforeIndex: number,
    _afterIndex: number,
    jingleCollectionJinglesListReordered: JingleCollectionJingleWithStatus[],
  ) => {
    if (jingleCollectionData === null) return

    const originalIndexMap = new Map<string, number>()
    jingleCollectionJinglesList.forEach((jingle, idx) => {
      originalIndexMap.set(jingle.id, idx)
    })

    const changedJingles: CreateJingleCollectionItemModel[] = []

    jingleCollectionJinglesListReordered.forEach((jingle, newIndex) => {
      const oldIndex = originalIndexMap.get(jingle.id)
      if (oldIndex === undefined) return
      if (oldIndex !== newIndex) {
        changedJingles.push({
          id: jingle.id,
          number: newIndex,
        })
      }
    })

    reorderJingleCollectionJingles(jingleCollectionJinglesListReordered)

    updateJinglePositionInJingleCollection.start({
      id: jingleCollectionData.id,
      data: { items: changedJingles },
    })
  }

  // Jingle Form Modal
  const removeJingleFromCollection = useUnit(jingleRemovedFromCollection)
  const handleRemove = (bindId: string) => {
    removeJingleFromCollection({ bindId })
  }

  // Table Columns
  const jingleCollectionJinglesReorderBlocked = useUnit(
    $jingleCollectionJinglesReorderBlocked,
  )
  const columns: ProColumns<JingleCollectionJingleWithStatus>[] = [
    {
      dataIndex: 'sort',
      width: COMMON_COLUMN_WIDTH.DRAG,
      render: () => null,
      onCell: () => ({
        style: {
          backgroundColor: '#fafafa',
          pointerEvents: jingleCollectionJinglesReorderBlocked
            ? 'none'
            : 'auto',
          opacity: jingleCollectionJinglesReorderBlocked ? 0.3 : 1,
        },
      }),
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      width: '100%',
      render: (_, jingle) => {
        if (jingle.fileUploadStatus === 'done') {
          if (jingle.uploadStatus === TrackUploadStatus.Uploading) {
            return (
              <Flex gap={16}>
                <Tooltip title="Файл не загружен">
                  <ExclamationCircleTwoTone
                    twoToneColor={antDesignThemeToken.colorError}
                  />
                </Tooltip>
                {jingle.name}
              </Flex>
            )
          } else if (
            jingle.uploadStatus === TrackUploadStatus.DeletionPending
          ) {
            return (
              <Flex gap={16}>
                <Tooltip title="Помечен на удаление">
                  <CloseCircleTwoTone
                    twoToneColor={antDesignThemeToken.colorWarning}
                  />
                </Tooltip>
                {jingle.name}
              </Flex>
            )
          } else return jingle.name
        } else {
          return (
            <Flex gap={12}>
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: '42px' }} />}
              />
              {jingle.name}
            </Flex>
          )
        }
      },
    },
    {
      title: '',
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, jingle) => {
        return (
          <Space size="middle">
            <Button
              onClick={() => onJingleEditButtonClick(jingle.trackId)}
              icon={<EditOutlined />}
            />
            <Button
              onClick={() => handleRemove(jingle.id)}
              icon={<CloseOutlined style={{ color: 'red' }} />}
            />
          </Space>
        )
      },
    },
  ]

  return (
    <>
      {jingleCollectionData !== null && (
        <>
          <JingleSelectionModal
            onApply={(ids) => {
              addJingleToCollection.start({
                id: jingleCollectionData.id,
                data: {
                  jingleIds: ids,
                },
              })
            }}
          />
          <HidingSpin
            spinning={
              (!jingleCollectionData && !jingleCollectionError) ||
              (!jingleCollectionJinglesData && !jingleCollectionJinglesError)
            }
          >
            {jingleCollectionJinglesList.length ? (
              <>
                <Flex vertical gap={8} style={{ width: '100%' }}>
                  <DragSortTable
                    options={false}
                    columns={columns}
                    rowKey="id"
                    search={false}
                    pagination={false}
                    dataSource={jingleCollectionJinglesList}
                    dragSortKey="sort"
                    dragSortHandlerRender={TableDragHandle}
                    onDragSortEnd={handleDragSortEnd}
                  />
                  <Flex style={{ width: '100%' }} gap={8}>
                    <Button
                      style={{ width: '50%' }}
                      type="dashed"
                      onClick={() => {
                        openJingleSelectionModal()
                      }}
                      icon={<PlusOutlined />}
                    >
                      Добавить джинглы
                    </Button>
                    <Button
                      style={{ width: '50%' }}
                      type="dashed"
                      onClick={() => {
                        if (!uploadRef.current) return
                        uploadRef.current.click()
                      }}
                      icon={<PlusOutlined />}
                    >
                      Загрузить джинглы
                    </Button>
                  </Flex>
                </Flex>
              </>
            ) : (
              <EmptyContent
                description="В списке пока нет ни одного джингла"
                content={
                  <Space>
                    <PrimaryButton
                      type="default"
                      onClick={() => {
                        openJingleSelectionModal()
                      }}
                    >
                      Добавить джинглы
                    </PrimaryButton>
                    <Button
                      type="default"
                      onClick={() => {
                        if (!uploadRef.current) return
                        uploadRef.current.click()
                      }}
                    >
                      Загрузить джинглы
                    </Button>
                  </Space>
                }
              />
            )}
            <Upload
              multiple
              accept=".mp3"
              beforeUpload={(_file, fileList) => {
                const fileListUids = fileList.map((file) => file.uid)
                if (fileListUids.every((uid) => typeof uid === 'string')) {
                  addFiles(fileList)
                }
                return false
              }}
              fileList={[]}
              showUploadList={false}
              style={{ width: '100%', display: 'none' }}
            >
              <div ref={uploadRef} />
            </Upload>
          </HidingSpin>
        </>
      )}
    </>
  )
}
